import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { AdminLanguageProvider, useAdminLanguage } from '../../contexts/AdminLanguageContext';
import {
  AlertCircle,
  Briefcase,
  Calendar,
  CheckCircle,
  Eye,
  MapPin,
  ShieldCheck,
  Upload,
  X
} from 'lucide-react';

const MAX_CV_SIZE_BYTES = 10 * 1024 * 1024;
const ALLOWED_EXTENSIONS = ['pdf', 'docx'];
const ALLOWED_MIME_TYPES = [
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
];

const getFileExtension = (filename = '') => {
  const parts = filename.toLowerCase().split('.');
  return parts.length > 1 ? parts.pop() : '';
};

const formatFileSize = (bytes = 0) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const getDateLocale = (language) => {
  if (language === 'fr') return 'fr-FR';
  if (language === 'es') return 'es-ES';
  return 'en-US';
};

const mapSubmitError = ({ code, fallback, t }) => {
  const byCode = {
    APPLICATION_RATE_LIMITED: t('jobApplication.error.general'),
    CV_FILE_TOO_LARGE: t('jobApplication.validation.cvFileSizeError'),
    CV_INVALID_FILE_TYPE: t('jobApplication.validation.cvFileTypeError'),
    APPLICATION_CV_REQUIRED: t('jobApplication.validation.cvRequired'),
    APPLICATION_OFFER_NOT_FOUND: t('jobApplication.expired.message'),
    APPLICATION_SUBMIT_FAILED: t('jobApplication.error.general')
  };
  return byCode[code] || fallback || t('jobApplication.error.general');
};

const FullPageMessage = ({ icon: Icon, title, message, action = null, tone = 'error' }) => {
  const toneClasses = tone === 'success'
    ? 'text-green-600 bg-green-100 border-green-200'
    : 'text-red-600 bg-red-100 border-red-200';

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center px-4">
      <div className="w-full max-w-lg rounded-2xl border border-slate-200 bg-white p-8 shadow-sm text-center">
        <div className={`mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-xl border ${toneClasses}`}>
          <Icon className="h-6 w-6" />
        </div>
        <h2 className="text-2xl font-semibold text-slate-900">{title}</h2>
        <p className="mt-3 text-sm leading-6 text-slate-600">{message}</p>
        {action ? <div className="mt-6">{action}</div> : null}
      </div>
    </div>
  );
};

const LoadingState = () => {
  const { t } = useAdminLanguage();
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
        <div className="animate-pulse space-y-6">
          <div className="h-40 rounded-2xl border border-slate-200 bg-white" />
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
            <div className="lg:col-span-7 space-y-6">
              <div className="h-56 rounded-2xl border border-slate-200 bg-white" />
              <div className="h-40 rounded-2xl border border-slate-200 bg-white" />
            </div>
            <div className="lg:col-span-5">
              <div className="h-[540px] rounded-2xl border border-slate-200 bg-white" />
            </div>
          </div>
        </div>
        <p className="mt-6 text-center text-sm text-slate-500">{t('jobApplication.loading')}</p>
      </div>
    </div>
  );
};

const ErrorMessage = () => {
  const { t } = useAdminLanguage();
  return (
    <FullPageMessage
      icon={AlertCircle}
      title={t('jobApplication.error.title')}
      message={t('jobApplication.error.message')}
      action={(
        <Link
          to="/"
          className="inline-flex items-center justify-center rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800"
        >
          {t('jobApplication.buttons.backToHome')}
        </Link>
      )}
    />
  );
};

const ExpiredOfferMessage = () => {
  const { t } = useAdminLanguage();
  return (
    <FullPageMessage
      icon={AlertCircle}
      title={t('jobApplication.expired.title')}
      message={t('jobApplication.expired.message')}
      action={(
        <Link
          to="/"
          className="inline-flex items-center justify-center rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800"
        >
          {t('jobApplication.buttons.backToHome')}
        </Link>
      )}
    />
  );
};

const JobApplicationContent = ({ offer, company, publicUrl }) => {
  const { t, currentLanguage } = useAdminLanguage();
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [file, setFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [errors, setErrors] = useState({});

  const publishDate = useMemo(() => {
    if (!offer?.published_at) return null;
    return new Date(offer.published_at).toLocaleDateString(getDateLocale(currentLanguage));
  }, [offer?.published_at, currentLanguage]);

  const updateField = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const validate = () => {
    const next = {};
    if (!formData.first_name.trim()) next.first_name = t('jobApplication.validation.firstNameRequired');
    if (!formData.last_name.trim()) next.last_name = t('jobApplication.validation.lastNameRequired');
    if (!formData.email.trim()) {
      next.email = t('jobApplication.validation.emailRequired');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      next.email = t('jobApplication.validation.emailInvalid');
    }
    if (!file) next.cv = t('jobApplication.validation.cvRequired');
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const validateAndSetFile = (selected) => {
    if (!selected) return;

    const extension = getFileExtension(selected.name);
    const mime = (selected.type || '').toLowerCase();
    const validByExtension = ALLOWED_EXTENSIONS.includes(extension);
    const validByMime = !mime || ALLOWED_MIME_TYPES.includes(mime);

    if (!validByExtension || !validByMime) {
      setErrors((prev) => ({ ...prev, cv: t('jobApplication.validation.cvFileTypeError') }));
      return;
    }

    if (selected.size > MAX_CV_SIZE_BYTES) {
      setErrors((prev) => ({ ...prev, cv: t('jobApplication.validation.cvFileSizeError') }));
      return;
    }

    setFile(selected);
    setErrors((prev) => ({ ...prev, cv: '' }));
  };

  const handleFileChange = (event) => {
    validateAndSetFile(event.target.files?.[0]);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setDragActive(false);
    validateAndSetFile(event.dataTransfer?.files?.[0]);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validate()) return;

    setSubmitting(true);
    setErrors((prev) => ({ ...prev, submit: '' }));

    try {
      const payload = new FormData();
      payload.append('public_url', publicUrl);
      payload.append('first_name', formData.first_name.trim());
      payload.append('last_name', formData.last_name.trim());
      payload.append('email', formData.email.trim());
      payload.append('phone', formData.phone.trim());
      payload.append('message', formData.message.trim());
      payload.append('cv', file);

      const response = await fetch('/api/applications/submit', {
        method: 'POST',
        body: payload
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        if (Array.isArray(data.details) && data.details.length > 0) {
          const fieldErrors = {};
          data.details.forEach((detail) => {
            const field = detail.path || detail.param;
            if (field && !fieldErrors[field]) {
              fieldErrors[field] = detail.msg || t('jobApplication.error.general');
            }
          });
          setErrors((prev) => ({ ...prev, ...fieldErrors }));
        }

        throw new Error(
          mapSubmitError({
            code: data.code,
            fallback: data.error || (data.details && data.details[0]?.msg),
            t
          })
        );
      }

      setSuccess(true);
    } catch (error) {
      setErrors((prev) => ({ ...prev, submit: error.message || t('jobApplication.error.general') }));
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <FullPageMessage
        icon={CheckCircle}
        tone="success"
        title={t('jobApplication.success.title')}
        message={t('jobApplication.success.description')}
        action={(
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800"
          >
            {t('jobApplication.buttons.backToHome')}
          </Link>
        )}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
        <section className="rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="grid grid-cols-1 gap-6 px-6 py-8 lg:grid-cols-12 lg:px-8">
            <div className="lg:col-span-8">
              <div className="inline-flex items-center rounded-full border border-blue-200 bg-blue-50 px-3 py-1 text-xs font-medium text-blue-700">
                <Briefcase className="mr-1.5 h-3.5 w-3.5" />
                {company?.company_name || offer.client || 'Candio'}
              </div>
              <h1 className="mt-3 text-3xl font-semibold leading-tight text-slate-900 sm:text-4xl">
                {offer.title}
              </h1>
              <div className="mt-4 flex flex-wrap gap-3 text-sm text-slate-600">
                {offer.location ? (
                  <span className="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-3 py-1">
                    <MapPin className="mr-1.5 h-4 w-4" />
                    {offer.location}
                  </span>
                ) : null}
                {publishDate ? (
                  <span className="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-3 py-1">
                    <Calendar className="mr-1.5 h-4 w-4" />
                    {t('jobApplication.offer.publishedOn')} {publishDate}
                  </span>
                ) : null}
                <span className="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-3 py-1">
                  <Eye className="mr-1.5 h-4 w-4" />
                  {offer.views_count} {t('jobApplication.offer.views')}
                </span>
              </div>
            </div>
            <div className="flex items-start justify-start lg:col-span-4 lg:justify-end">
              {company?.company_logo_url ? (
                <img
                  src={company.company_logo_url}
                  alt={company.company_name || 'Company logo'}
                  className="h-14 max-w-[220px] object-contain"
                />
              ) : (
                <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
                  {company?.company_name || offer.client || 'Candio'}
                </div>
              )}
            </div>
          </div>
        </section>

        <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-12">
          <div className="space-y-6 lg:col-span-7">
            <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <h2 className="text-lg font-semibold text-slate-900">{t('jobApplication.offer.jobDescription')}</h2>
              <div className="mt-4 whitespace-pre-wrap text-sm leading-7 text-slate-700">
                {offer.description || '-'}
              </div>
            </section>

            {offer.skills ? (
              <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                <h3 className="text-lg font-semibold text-slate-900">{t('jobApplication.offer.requiredSkills')}</h3>
                <div className="mt-4 whitespace-pre-wrap text-sm leading-7 text-slate-700">
                  {offer.skills}
                </div>
              </section>
            ) : null}
          </div>

          <aside className="lg:col-span-5">
            <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm lg:sticky lg:top-6">
              <h2 className="text-lg font-semibold text-slate-900">{t('jobApplication.form.title')}</h2>
              <p className="mt-2 text-sm text-slate-600">{t('jobApplication.form.subtitle')}</p>

              <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50 p-3">
                <div className="grid grid-cols-1 gap-2 text-xs text-slate-600 sm:grid-cols-3">
                  <span className="inline-flex items-center rounded-md bg-white px-2 py-1 font-medium text-slate-700">
                    {t('jobApplication.steps.step')} 1 {t('jobApplication.steps.of')} 3
                  </span>
                  <span className="inline-flex items-center rounded-md bg-white px-2 py-1">{t('jobApplication.steps.personalInfo')}</span>
                  <span className="inline-flex items-center rounded-md bg-white px-2 py-1">{t('jobApplication.steps.yourCV')}</span>
                </div>
              </div>

              {errors.submit ? (
                <div className="mt-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                  {errors.submit}
                </div>
              ) : null}

              <form className="mt-4 space-y-4" onSubmit={handleSubmit}>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label className="block text-sm font-medium text-slate-700">{t('jobApplication.form.firstName')}</label>
                    <input
                      type="text"
                      name="first_name"
                      value={formData.first_name}
                      onChange={(event) => updateField(event.target.name, event.target.value)}
                      className="mt-1 h-10 w-full rounded-lg border border-slate-300 px-3 text-sm text-slate-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                      placeholder={t('jobApplication.placeholders.firstName')}
                    />
                    {errors.first_name ? <p className="mt-1 text-xs text-red-600">{errors.first_name}</p> : null}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700">{t('jobApplication.form.lastName')}</label>
                    <input
                      type="text"
                      name="last_name"
                      value={formData.last_name}
                      onChange={(event) => updateField(event.target.name, event.target.value)}
                      className="mt-1 h-10 w-full rounded-lg border border-slate-300 px-3 text-sm text-slate-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                      placeholder={t('jobApplication.placeholders.lastName')}
                    />
                    {errors.last_name ? <p className="mt-1 text-xs text-red-600">{errors.last_name}</p> : null}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700">{t('jobApplication.form.email')}</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={(event) => updateField(event.target.name, event.target.value)}
                    className="mt-1 h-10 w-full rounded-lg border border-slate-300 px-3 text-sm text-slate-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                    placeholder={t('jobApplication.placeholders.email')}
                  />
                  {errors.email ? <p className="mt-1 text-xs text-red-600">{errors.email}</p> : null}
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700">{t('jobApplication.form.phone')}</label>
                  <input
                    type="text"
                    name="phone"
                    value={formData.phone}
                    onChange={(event) => updateField(event.target.name, event.target.value)}
                    className="mt-1 h-10 w-full rounded-lg border border-slate-300 px-3 text-sm text-slate-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                    placeholder={t('jobApplication.placeholders.phone')}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700">{t('jobApplication.form.message')}</label>
                  <textarea
                    name="message"
                    rows={4}
                    value={formData.message}
                    onChange={(event) => updateField(event.target.name, event.target.value)}
                    className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                    placeholder={t('jobApplication.form.messagePlaceholder')}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700">{t('jobApplication.form.cv')}</label>
                  <div
                    onDrop={handleDrop}
                    onDragOver={(event) => {
                      event.preventDefault();
                      setDragActive(true);
                    }}
                    onDragLeave={() => setDragActive(false)}
                    className={`mt-2 rounded-xl border-2 border-dashed p-4 transition ${
                      dragActive
                        ? 'border-blue-400 bg-blue-50'
                        : 'border-slate-300 bg-slate-50'
                    }`}
                  >
                    <input
                      type="file"
                      accept=".pdf,.docx"
                      onChange={handleFileChange}
                      className="hidden"
                      id="job-application-cv-upload"
                    />

                    {file ? (
                      <div className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 bg-white px-3 py-2">
                        <div className="min-w-0">
                          <p className="truncate text-sm font-medium text-slate-800">{file.name}</p>
                          <p className="text-xs text-slate-500">{formatFileSize(file.size)}</p>
                        </div>
                        <button
                          type="button"
                          onClick={() => setFile(null)}
                          className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-slate-200 text-slate-500 hover:bg-slate-50 hover:text-slate-700"
                          aria-label={t('jobApplication.buttons.remove')}
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ) : (
                      <label htmlFor="job-application-cv-upload" className="block cursor-pointer text-center">
                        <Upload className="mx-auto h-6 w-6 text-blue-600" />
                        <p className="mt-2 text-sm font-medium text-slate-700">{t('jobApplication.form.cvUploadText')}</p>
                        <p className="mt-1 text-xs text-slate-500">{t('jobApplication.form.cvFileTypes')}</p>
                      </label>
                    )}
                  </div>
                  {errors.cv ? <p className="mt-1 text-xs text-red-600">{errors.cv}</p> : null}
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  className="inline-flex h-11 w-full items-center justify-center rounded-lg bg-blue-600 px-4 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {submitting ? t('jobApplication.form.submitting') : t('jobApplication.form.submit')}
                </button>

                <p className="inline-flex items-start text-xs leading-5 text-slate-500">
                  <ShieldCheck className="mr-1.5 mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-400" />
                  {t('jobApplication.form.privacy')}
                </p>
              </form>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
};

const JobApplicationPage = () => {
  const { publicUrl } = useParams();
  const [workspaceId, setWorkspaceId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [expired, setExpired] = useState(false);
  const [offer, setOffer] = useState(null);
  const [company, setCompany] = useState(null);

  useEffect(() => {
    const fetchOffer = async () => {
      try {
        setLoading(true);
        const response = await fetch(`/api/offers/public/${publicUrl}`);
        const data = await response.json();

        if (!response.ok) {
          if (response.status === 404) {
            setExpired(true);
            setWorkspaceId(data.workspace_id || 1);
          } else {
            setError(true);
            setWorkspaceId(data.workspace_id || 1);
          }
          return;
        }

        setOffer(data.offer);
        setCompany(data.company);
        setWorkspaceId(data.offer.workspace_id);
      } catch (err) {
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchOffer();
  }, [publicUrl]);

  return (
    <AdminLanguageProvider workspaceId={workspaceId || 1}>
      {loading ? (
        <LoadingState />
      ) : expired ? (
        <ExpiredOfferMessage />
      ) : error || !offer ? (
        <ErrorMessage />
      ) : (
        <JobApplicationContent offer={offer} company={company} publicUrl={publicUrl} />
      )}
    </AdminLanguageProvider>
  );
};

export default JobApplicationPage;
